import type { DeckList } from '../types/protocol'
import { asCardFoil } from '../types/foil'

/**
 * Construction limits used by the Deck Builder and by the match handshake.
 * Extra-15 is a house rule (see docs/era-rules.md); main 40–60 is official.
 */
export const DECK_LIMITS = {
  MAIN_MIN: 40,
  MAIN_MAX: 60,
  EXTRA_MAX: 15,
  MAX_COPIES: 3,
} as const

/**
 * Temporary house copy caps for the Metal Raiders window.
 * While this map has entries it applies to every duel: ranked, casual,
 * private rooms, and versus the CPU, including the player's list and
 * every CPU recipe.
 * A saved list may still store up to MAX_COPIES; starting a duel checks the cap.
 * An empty map means no house banlist is active.
 */
export const PVP_TEMP_LIMITS: Readonly<Record<string, number>> = {
  // Limited 1
  '55144522': 1, // Pot of Greed
  '83764718': 1, // Monster Reborn
  '12580477': 1, // Raigeki
  '4031928': 1, // Change of Heart
  '53129443': 1, // Dark Hole
  '44095762': 1, // Mirror Force
  '19613556': 1, // Heavy Storm
  '80604091': 1, // Ultimate Offering
  '4206964': 1, // Trap Hole
  '72302403': 1, // Swords of Revealing Light
  '12607053': 1, // Waboku
  '70781052': 1, // Summoned Skull
  '26202165': 1, // Sangan
  '78010363': 1, // Witch of the Black Forest
  '54652250': 1, // Man-Eater Bug
  '13945283': 1, // Wall of Illusion
  '66788016': 1, // Fissure
  '97590747': 1, // La Jinn the Mystical Genie of the Lamp
  '41420027': 1, // Solemn Judgment
  '3819470': 1, // Seven Tools of the Bandit
  '31560081': 1, // Magician of Faith
  '94773007': 1, // Jirai Gumo
  // Semi-Limited 2
  '77414722': 2, // Magic Jammer
  '23771716': 2, // 7 Colored Fish
  '88279736': 2, // Robbin' Goblin
  '79759861': 2, // Tribute to the Doomed
}

/** Live house caps. Seeded from {@link PVP_TEMP_LIMITS}; the server replaces them from the database. */
let houseLimitsLive: Record<string, number> = { ...PVP_TEMP_LIMITS }

/** Caps in force right now. An empty map means no house banlist. */
export function houseLimits(): Readonly<Record<string, number>> {
  return houseLimitsLive
}

/** Replace the live caps. Unknown or out-of-range values are dropped. */
export function setHouseLimits(next: Readonly<Record<string, number>>): void {
  const clean: Record<string, number> = {}
  for (const [id, max] of Object.entries(next)) {
    if (max === 1 || max === 2) clean[id] = max
  }
  houseLimitsLive = clean
}

export function houseBanlistActive(): boolean {
  return Object.keys(houseLimitsLive).length > 0
}

export type BanlistViolation = {
  id: string
  count: number
  max: number
}

function asIdArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null
  const ids: string[] = []
  for (const item of value) {
    if (typeof item === 'string' && item.length > 0) ids.push(item)
    else if (typeof item === 'number' && Number.isFinite(item)) ids.push(String(item))
    else return null
  }
  return ids
}

function asFoilArray(value: unknown, length: number): string[] {
  const foils: string[] = []
  const raw = Array.isArray(value) ? value : []
  for (let i = 0; i < length; i++) {
    foils.push(asCardFoil(raw[i]))
  }
  return foils
}

/**
 * Coerces a socket/JSON payload into a DeckList. Returns null if the shape is
 * not two arrays of card IDs. Missing foil lists default to Rare.
 */
export function parseDeckList(raw: unknown): DeckList | null {
  if (!raw || typeof raw !== 'object') return null
  const rec = raw as Record<string, unknown>
  const main = asIdArray(rec.main)
  if (!main) return null
  const extra = rec.extra === undefined ? [] : asIdArray(rec.extra)
  if (!extra) return null
  return {
    main,
    extra,
    mainFoils: asFoilArray(rec.mainFoils, main.length),
    extraFoils: asFoilArray(rec.extraFoils, extra.length),
  }
}

export type DeckLegalSize = {
  minMain?: number
  maxCopies?: number
}

function maxCopiesFor(
  id: string,
  copyLimits?: Readonly<Record<string, number>>,
  defaultMax: number = DECK_LIMITS.MAX_COPIES,
): number {
  const listed = copyLimits?.[id]
  if (typeof listed === 'number') return listed
  return defaultMax
}

/**
 * Official construction checks plus an optional Extra-only ID set (fusions).
 * `legalIds` is the full LOB catalog; `extraOnlyIds` rejects Extra cards in
 * the Main Deck and non-Extra cards in the Extra Deck.
 */
function idsPassCopyAndCatalog(
  ids: string[],
  legalIds: ReadonlySet<string>,
  counts: Map<string, number>,
  extraRule: (id: string) => boolean,
  copyLimits?: Readonly<Record<string, number>>,
  defaultMax: number = DECK_LIMITS.MAX_COPIES,
): boolean {
  for (const id of ids) {
    if (!legalIds.has(id) || !extraRule(id)) return false
    const next = (counts.get(id) ?? 0) + 1
    if (next > maxCopiesFor(id, copyLimits, defaultMax)) return false
    counts.set(id, next)
  }
  return true
}

function zoneAndCopyOk(
  deck: DeckList,
  legalIds: ReadonlySet<string>,
  extraOnlyIds: ReadonlySet<string> | undefined,
  copyLimits?: Readonly<Record<string, number>>,
  defaultMax: number = DECK_LIMITS.MAX_COPIES,
): boolean {
  if (deck.main.length > DECK_LIMITS.MAIN_MAX) return false
  if (deck.extra.length > DECK_LIMITS.EXTRA_MAX) return false
  const counts = new Map<string, number>()
  const mainOk = (id: string) => !extraOnlyIds?.has(id)
  const extraOk = (id: string) => !extraOnlyIds || extraOnlyIds.has(id)
  return (
    idsPassCopyAndCatalog(deck.main, legalIds, counts, mainOk, copyLimits, defaultMax) &&
    idsPassCopyAndCatalog(deck.extra, legalIds, counts, extraOk, copyLimits, defaultMax)
  )
}

export function isLegalDeckList(
  deck: DeckList,
  legalIds: ReadonlySet<string>,
  extraOnlyIds?: ReadonlySet<string>,
  copyLimits?: Readonly<Record<string, number>>,
  size?: DeckLegalSize,
): boolean {
  const minMain = size?.minMain ?? DECK_LIMITS.MAIN_MIN
  if (deck.main.length < minMain) return false
  return zoneAndCopyOk(
    deck,
    legalIds,
    extraOnlyIds,
    copyLimits,
    size?.maxCopies ?? DECK_LIMITS.MAX_COPIES,
  )
}

/**
 * WIP save: catalog / Extra-zone / copy limits, no 40-card minimum.
 */
export function isSavableDeckList(
  deck: DeckList,
  legalIds: ReadonlySet<string>,
  extraOnlyIds?: ReadonlySet<string>,
): boolean {
  return zoneAndCopyOk(deck, legalIds, extraOnlyIds)
}

/**
 * Cards in `PVP_TEMP_LIMITS` whose main+extra count exceeds the temporary cap.
 */
export function pvpTempBanlistViolations(
  deck: Pick<DeckList, 'main' | 'extra'>,
): BanlistViolation[] {
  const counts = new Map<string, number>()
  for (const id of deck.main) {
    counts.set(id, (counts.get(id) ?? 0) + 1)
  }
  for (const id of deck.extra) {
    counts.set(id, (counts.get(id) ?? 0) + 1)
  }
  const out: BanlistViolation[] = []
  for (const [id, max] of Object.entries(houseLimits())) {
    const count = counts.get(id) ?? 0
    if (count > max) out.push({ id, count, max })
  }
  return out
}
